/**
 * المرحلة 1.35 — the centre's paper as the screens draw it: the dotted line
 * with its label, the sheet's head with the centre's logo, the print button
 * and the weekday the paper writes beside a date. The child file, the stay
 * booklet, the account and the consents are all this same paper.
 */

import { Button } from '../../components/common';
import { usePermissions } from '../../context/PermissionContext';
import { useI18n } from '../../i18n/I18nProvider';
import { activeCountry } from '../../domain/locale';
import { pickLogo } from '../../domain/logoBackdrop';
import { printArea } from '../hospitalShared/labels';

/** «اليوم» on the paper: the weekday of a date, on the institution's clock. */
export function weekday(value, lang) {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(lang === 'en' ? 'en-GB' : 'ar-EG', {
      weekday: 'long',
      timeZone: activeCountry().timezone,
    }).format(new Date(value));
  } catch {
    return '';
  }
}

/** A dotted line of the paper: its label, and the value written on it (or left blank). */
export function Line({ label, children, wide = false, testId }) {
  return (
    <div className={`cf-line${wide ? ' cf-line--wide' : ''}`} data-testid={testId}>
      <span className="cf-line__label">{label}</span>
      <span className="cf-line__value">{children || ' '}</span>
    </div>
  );
}

/** The sheet's head: the centre's logo (the light one, it is paper) and its name over the sheet's title. */
export function SheetHead({ title, subtitle }) {
  const { institutionName, institutionLogo, institutionLogoDark, institutionLogoBackdrop } = usePermissions();
  const logo = pickLogo(
    {
      logo: institutionLogo,
      logoDark: institutionLogoDark,
      backdrop: institutionLogoBackdrop,
    },
    { dark: false },
  );
  return (
    <header className="cf-head">
      {logo.src && (
        <span className={`cf-head__logo logo-well${logo.plate ? ' logo-well--plate' : ''}`}>
          <img src={logo.src} alt="" />
        </span>
      )}
      <div className="cf-head__text">
        {institutionName && <p className="cf-head__centre">{institutionName}</p>}
        <h3 className="cf-head__title">{title}</h3>
        {subtitle && <p className="cf-head__sub">{subtitle}</p>}
      </div>
    </header>
  );
}

/**
 * The print button of a sheet. The centre's papers are A4 upright, as its
 * booklet is: a sheet laid out wide in a large window is scaled to the page's
 * width rather than turned on its side.
 */
export function PrintButton({ target, label, testId = 'child-print' }) {
  const { t } = useI18n();
  return (
    <Button variant="ghost" icon="printer" onClick={() => target.current && printArea(target.current, { orientation: 'portrait' })} data-testid={testId}>
      {label || t('childFile.print')}
    </Button>
  );
}
